import React, { useState } from 'react';
import { submitPartnershipRequest } from '../../firebase/partnershipService';
import './PartnershipForm.css';

/**
 * Partnership Contact Form Component
 * Used to gate access to API documentation
 */
const PartnershipForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    name: '',
    phone: '',
    message: '',
    useCase: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error message when user starts typing
    if (message && !isSuccess) {
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.companyName.trim()) {
      setMessage('Company name is required');
      setIsSuccess(false);
      return;
    }

    if (!formData.email.trim()) {
      setMessage('Email is required');
      setIsSuccess(false);
      return;
    }

    if (!formData.name.trim()) {
      setMessage('Your name is required');
      setIsSuccess(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('Please enter a valid email address');
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const docId = await submitPartnershipRequest(formData);
      console.log('Successfully submitted partnership request with ID:', docId);
      
      // Store email in localStorage for quick access check
      localStorage.setItem('partnership_request_email', formData.email.toLowerCase().trim());
      localStorage.setItem('partnership_request_submitted', 'true');
      localStorage.setItem('partnership_request_timestamp', new Date().toISOString());
      
      setMessage('Thank you! Your partnership request has been submitted. We\'ll review it and get back to you soon.');
      setIsSuccess(true);
      
      // Call onSuccess callback after a short delay
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting partnership request:', error);
      setMessage(error.message || 'Sorry, there was an error submitting your request. Please try again.');
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="partnership-form-container">
      <div className="partnership-form-header">
        <h2 className="partnership-form-title">Request API Access</h2>
        <p className="partnership-form-subtitle">
          To access our API documentation and integrate with WeSplit, please fill out the form below. 
          We'll review your request and grant access to approved partners.
        </p>
      </div>

      <form className="partnership-form" onSubmit={handleSubmit}>
        <div className="partnership-form-group">
          <label htmlFor="companyName" className="partnership-form-label">
            Company Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            className="partnership-form-input"
            placeholder="Enter your company name"
            required
            disabled={isLoading || isSuccess}
          />
        </div>

        <div className="partnership-form-group">
          <label htmlFor="name" className="partnership-form-label">
            Your Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="partnership-form-input"
            placeholder="Enter your full name"
            required
            disabled={isLoading || isSuccess}
          />
        </div>

        <div className="partnership-form-group">
          <label htmlFor="email" className="partnership-form-label">
            Email Address <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="partnership-form-input"
            placeholder="your.email@company.com"
            required
            disabled={isLoading || isSuccess}
          />
        </div>

        <div className="partnership-form-group">
          <label htmlFor="phone" className="partnership-form-label">
            Phone Number <span className="optional">(Optional)</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="partnership-form-input"
            placeholder="+1 (555) 123-4567"
            disabled={isLoading || isSuccess}
          />
        </div>

        <div className="partnership-form-group">
          <label htmlFor="useCase" className="partnership-form-label">
            Use Case <span className="optional">(Optional)</span>
          </label>
          <textarea
            id="useCase"
            name="useCase"
            value={formData.useCase}
            onChange={handleChange}
            className="partnership-form-textarea"
            placeholder="Describe how you plan to use the WeSplit API (e.g., e-commerce integration, payment processing, etc.)"
            rows={3}
            disabled={isLoading || isSuccess}
          />
        </div>

        <div className="partnership-form-group">
          <label htmlFor="message" className="partnership-form-label">
            Additional Message <span className="optional">(Optional)</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="partnership-form-textarea"
            placeholder="Any additional information you'd like to share"
            rows={3}
            disabled={isLoading || isSuccess}
          />
        </div>

        {message && (
          <div className={`partnership-form-message ${isSuccess ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <button
          type="submit"
          className="partnership-form-submit"
          disabled={isLoading || isSuccess}
        >
          {isLoading ? 'Submitting...' : isSuccess ? 'Submitted!' : 'Submit Request'}
        </button>

        {isSuccess && (
          <p className="partnership-form-note">
            Note: Access to the API documentation will be granted after we review your request. 
            You'll receive an email notification once access is approved.
          </p>
        )}
      </form>
    </div>
  );
};

export default PartnershipForm;
